import { Component, Inject, ViewChild } from '@angular/core'
import { MapService } from 'src/app/core/services/map.service'
import { Observable, Subject, debounceTime, interval, takeUntil } from 'rxjs'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AppPaths } from 'src/app/app-routing.module'
import { Layers } from 'src/app/shared/layers-config/layers'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Permissions } from 'src/app/core/enums/permissions.enum'
import {
  provider1ObservationsLayerProvider,
  provider2ObservationsLayerProvider,
  constructionSitesLayerProvider,
  clusteredSignsLayerProvider,
  matchedSignsLayerProvider,
  blacklistAreaLayerProvider,
  roadCoverageLayerProvider,
  speedProfileLayerProvider,
} from 'src/app/shared/providers/internal-layers-provider'
import { nonDetectionsLayerProvider } from 'src/app/shared/providers/non-detections-layer.provider'
import { LayerObservationsService } from 'src/app/shared/layers/layer-observations.service'
import { LayerRolloutsTileService } from 'src/app/shared/layers/layer-rollouts-map-tile.service'
import { DataProviderInfoService } from 'src/app/shared/services/data-provider-info.service'

import { LayerClusteredSignsService } from 'src/app/shared/layers/layer-clustered-signs.service'
import { LayerMatchedSignsService } from 'src/app/shared/layers/layer-matched-sign.service'
import { ProviderInfo } from 'src/app/shared/models/provider-info.model'
import { LayerRolloutsMatchedSignsTileService } from 'src/app/shared/layers/layer-rollouts-matched-sign.service'
import { LayerRolloutsFilteredMatchedSignsTileService } from 'src/app/shared/layers/layer-rollouts-filtered-matched-sign.service'
import { DataRolloutTilesService } from 'src/app/shared/services/data-rollout-tiles-service'
import { BlacklistAreaDialogComponent } from '../components/blacklist-area-dialog/blacklist-area-dialog.component'
import { LayerBlacklistAreaService } from 'src/app/shared/layers/layer-blacklist-area.service'
import { DrawService, DrawnPolygon } from '../services/draw.service'
import { LayerConstructionSitesService } from 'src/app/shared/layers/layer-construction-site.service'
import { DataConstructionSitesService } from 'src/app/shared/services/data-construction-sites.service'
import { LayerRoadCoverageService } from 'src/app/shared/layers/layer-road-coverage.service'
import { LayerSpeedProfileService } from 'src/app/shared/layers/layer-speed-profile.service'
import { LayerNonDetectionsService } from 'src/app/shared/layers/layer-non-detections.service'

@Component({
  selector: 'app-internal-page',
  templateUrl: './internal-page.component.html',
  providers: [
    LayerConstructionSitesService,
    LayerRolloutsTileService,
    LayerRolloutsMatchedSignsTileService,
    LayerRolloutsFilteredMatchedSignsTileService,
    LayerRoadCoverageService,
    LayerSpeedProfileService,
    DataRolloutTilesService,
    DataConstructionSitesService,
    DataProviderInfoService,
    provider1ObservationsLayerProvider,
    provider2ObservationsLayerProvider,
    constructionSitesLayerProvider,
    roadCoverageLayerProvider,
    speedProfileLayerProvider,
    nonDetectionsLayerProvider,
    clusteredSignsLayerProvider,
    nonDetectionsLayerProvider,
    matchedSignsLayerProvider,
    blacklistAreaLayerProvider,
    DrawService,
  ],
  standalone: false,
})
export class InternalPageComponent {
  public Permissions = Permissions
  public appPath = AppPaths.Internal
  private stopTimer = new Subject<void>()
  public isInLiveMode = false
  public autoRefreshIntervalInMs = 0
  public layerIds = Layers
  public isProvider1ObservationsLayerVisible: boolean = false
  public isProvider2ObservationsLayerVisible: boolean = false
  public isSpeedLimitObservationsLayerVisible: boolean = false
  public currentTimestamp: Date | undefined
  public currentTimespan: number | undefined
  public isHistoryVisible = false
  public isLegendVisible = false
  public ProviderInfo$!: Observable<ProviderInfo>

  @ViewChild(BlacklistAreaDialogComponent)
  private blacklistAreaDialogComponent!: BlacklistAreaDialogComponent

  constructor(
    private mapService: MapService,
    public appState: AppStateService,
    @Inject('constructionSitesLayer')
    public constructionSitesLayerService: LayerConstructionSitesService,
    private layerRolloutsTileService: LayerRolloutsTileService,
    private layerRolloutsMatchedSignsTileService: LayerRolloutsMatchedSignsTileService,
    private layerRolloutsFilteredMatchedSignsTileService: LayerRolloutsFilteredMatchedSignsTileService,
    @Inject('roadCoverageLayer')
    private layerRoadCoverageService: LayerRoadCoverageService,
    @Inject('speedProfileLayer')
    private layerSpeedProfileService: LayerSpeedProfileService,
    @Inject('provider1ObservationsLayer')
    public provider1ObservationsLayerService: LayerObservationsService,
    @Inject('provider2ObservationsLayer')
    public provider2ObservationsLayerService: LayerObservationsService,
    @Inject('nonDetectionsLayer')
    public nonDetectionsLayerService: LayerNonDetectionsService,
    @Inject('clusteredSignsLayer')
    public clusteredSignsLayerService: LayerClusteredSignsService,
    @Inject('matchedSignsLayer')
    public matchedSignsLayerService: LayerMatchedSignsService,
    private dataProviderInfoService: DataProviderInfoService,
    public drawService: DrawService,
    @Inject('blacklistAreaLayer')
    public layerBlacklistAreaService: LayerBlacklistAreaService,
  ) {
    this.mapService.activateGeoLocationSearch()
    this.mapService.setPosition(
      this.appState.getState().lat,
      this.appState.getState().lon,
      this.appState.getState().zoom,
    )
    if (this.appState.getState().historyEnabled) {
      this.isHistoryVisible = true
    }
    this.updateQueryParameters()
    this.mapService.mapViewChanged
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(120))
      .subscribe(() => {
        this.updateQueryParameters()
      })

    this.evaluateQueryParameters()
    this.appState.stateChanged.pipe(takeUntilDestroyed()).subscribe(() => {
      this.evaluateQueryParameters()
    })

    this.drawService.polygonDrawn$
      .pipe(takeUntilDestroyed())
      .subscribe(({ polygon, center }: DrawnPolygon) => {
        this.blacklistAreaDialogComponent.showBlacklistDialog(polygon, center)
      })
  }

  ngOnInit() {
    this.ProviderInfo$ = this.dataProviderInfoService.loadProviderInfo()
  }
  ngOnDestroy() {
    this.mapService.deactivateGeoLocationSearch()
    this.stopTimer.next()
    this.stopTimer.complete()
  }

  setLiveModeRefreshInterval(seconds: number) {
    this.autoRefreshIntervalInMs = seconds * 1000
    this.stopLiveMode()
    if (this.autoRefreshIntervalInMs > 0) {
      this.startLiveMode()
    }
  }

  startLiveMode() {
    this.isInLiveMode = true
    interval(this.autoRefreshIntervalInMs)
      .pipe(takeUntil(this.stopTimer))
      .subscribe(() => {
        this.provider1ObservationsLayerService.reload()
        this.provider2ObservationsLayerService.reload()
      })
  }

  stopLiveMode() {
    this.isInLiveMode = false
    this.stopTimer.next()
  }

  toggleHistory = () => {
    this.isHistoryVisible = !this.isHistoryVisible
    this.updateQueryParameters()
  }

  toggleLegend = () => {
    this.isLegendVisible = !this.isLegendVisible
  }

  handleHistoryClosed = () => {
    this.isHistoryVisible = false
    this.updateQueryParameters()
  }

  evaluateQueryParameters() {
    if (this.appState.getState().historyEnabled) {
      this.isHistoryVisible = true
    }

    const layers = this.appState.selectLayers()

    if (layers.some((layer) => layer === Layers.Provider1Observations.name)) {
      this.isProvider1ObservationsLayerVisible = true
    }
    if (layers.some((layer) => layer === Layers.Provider2Observations.name)) {
      this.isProvider2ObservationsLayerVisible = true
    }
  }

  updateQueryParameters() {
    this.appState.setState(AppPaths.Internal, {
      lat: this.mapService.map.getCenter().lat,
      lon: this.mapService.map.getCenter().lng,
      zoom: this.mapService.map.getZoom(),
      historyEnabled: this.isHistoryVisible ? true : undefined,
    })
  }
}
